import { useState, useEffect, useCallback } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import {
  Thermometer, RefreshCw, Plus, CheckCircle2, XCircle, AlertTriangle,
  FlaskConical, Wind, PackageOpen, Loader2, Power, Trash2, Printer
} from "lucide-react";

const TEST_META = {
  vacuum: { label: "Vacuum Test", icon: Wind, freq: "Daily" },
  bowie_dick: { label: "Bowie-Dick Test", icon: PackageOpen, freq: "Daily" },
  biological: { label: "Biological Indicator (BI)", icon: FlaskConical, freq: "Weekly (Saturday)" },
};

const ResultBadge = ({ result, biological }) => {
  if (!result) return <Badge className="bg-slate-100 text-slate-500">Not done</Badge>;
  if (result === "pass") return <Badge className="bg-emerald-50 text-emerald-700">{biological ? "Negative (−)" : "Pass"}</Badge>;
  return <Badge className="bg-red-50 text-red-700">{biological ? "Positive (+)" : "Fail"}</Badge>;
};

export default function AutoclaveLog() {
  const { user, token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const isAdmin = user?.role === "admin";

  const [machines, setMachines] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [compliance, setCompliance] = useState(null);
  const [cycles, setCycles] = useState([]);
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [tab, setTab] = useState("tests");

  const [cycleDialog, setCycleDialog] = useState(false);
  const [testDialog, setTestDialog] = useState(null); // test_type or null
  const [submitting, setSubmitting] = useState(false);
  const [cycleForm, setCycleForm] = useState({ machine_id: "", program: "", contents: "", chemical_indicator_pass: true, status: "completed", notes: "" });
  const [testForm, setTestForm] = useState({ machine_id: "", result: "pass", notes: "" });

  const autoclaves = machines.filter(m => m.type === "autoclave");

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [mRes, pRes, cRes, cyRes, tRes] = await Promise.all([
        axios.get(`${API}/sterilization/machines`, { headers }),
        axios.get(`${API}/sterilization/programs`, { headers }),
        axios.get(`${API}/sterilization/compliance`, { headers }),
        axios.get(`${API}/sterilization/cycles?month=${month}`, { headers }),
        axios.get(`${API}/sterilization/tests?month=${month}`, { headers }),
      ]);
      setMachines(mRes.data);
      setPrograms(pRes.data);
      setCompliance(cRes.data);
      setCycles(cyRes.data);
      setTests(tRes.data);
    } catch (e) {
      toast.error("Failed to load autoclave data");
    } finally {
      setLoading(false);
    }
  }, [month, token]);

  useEffect(() => { refresh(); }, [refresh]);

  const submitCycle = async () => {
    if (!cycleForm.machine_id || !cycleForm.program) {
      toast.error("Select machine and program");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/sterilization/cycles`, cycleForm, { headers });
      toast.success("Cycle logged");
      setCycleDialog(false);
      setCycleForm({ machine_id: "", program: "", contents: "", chemical_indicator_pass: true, status: "completed", notes: "" });
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to log cycle");
    } finally {
      setSubmitting(false);
    }
  };

  const submitTest = async () => {
    if (!testForm.machine_id) {
      toast.error("Select machine");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/sterilization/tests`, { ...testForm, test_type: testDialog }, { headers });
      if (res.data.machine_shutdown) {
        toast.error(res.data.recall_required
          ? "2nd POSITIVE BI — machine set OUT OF SERVICE. RECALL & re-sterilize all instruments since last negative BI, and call the engineer."
          : "2nd failure — machine set OUT OF SERVICE. Call the company engineer.", { duration: 12000 });
      } else if (testForm.result === "fail") {
        toast.warning("Failure recorded (attempt 1). Repeat the test — a 2nd failure will shut the machine down.", { duration: 8000 });
      } else {
        toast.success("Test recorded");
      }
      setTestDialog(null);
      setTestForm({ machine_id: "", result: "pass", notes: "" });
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to record test");
    } finally {
      setSubmitting(false);
    }
  };

  const reactivateMachine = async (m) => {
    try {
      await axios.patch(`${API}/sterilization/machines/${m.id}`, { status: "active", note: "Cleared by engineer" }, { headers });
      toast.success(`${m.code} reactivated`);
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const deleteRow = async (kind, id) => {
    try {
      await axios.delete(`${API}/sterilization/${kind}/${id}`, { headers });
      toast.success("Deleted");
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete");
    }
  };

  const monthOptions = (() => {
    const opts = [];
    const d = new Date();
    for (let i = 0; i < 12; i++) {
      opts.push(new Date(d.getFullYear(), d.getMonth() - i, 15).toISOString().slice(0, 7));
    }
    return opts;
  })();

  if (loading && !compliance) {
    return (
      <div className="flex items-center justify-center h-96" dir="ltr">
        <Loader2 className="w-8 h-8 animate-spin text-[#655dc6]" />
      </div>
    );
  }

  return (
    <div dir="ltr" className="space-y-5 text-left" data-testid="autoclave-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-[#655dc6] text-white flex items-center justify-center">
            <Thermometer className="w-6 h-6" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-[#0A0A0A] tracking-tight">Autoclave Log</h2>
            <p className="text-sm text-slate-500">Cycle records, Vacuum / Bowie-Dick / Biological Indicator tests</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Select value={month} onValueChange={setMonth}>
            <SelectTrigger className="w-36" data-testid="autoclave-month-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {monthOptions.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5 border-[#655dc6] text-[#655dc6] hover:bg-[#F3F2FA]"
            onClick={() => window.open(`/autoclave/print?month=${month}`, "_blank")}
            data-testid="print-monthly-report-btn"
          >
            <Printer className="w-4 h-4" /> Monthly Report
          </Button>
          <Button variant="outline" size="sm" onClick={refresh} disabled={loading} className="gap-1.5" data-testid="autoclave-refresh-btn">
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>
      </div>

      {/* Machine compliance cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {(compliance?.machines || []).map(({ machine, vacuum_today, bowie_dick_today, last_biological, cycles_today }) => {
          const down = machine.status === "out_of_service";
          return (
            <Card key={machine.id} className={`border rounded-xl shadow-none ${down ? "border-red-300 bg-red-50/50" : "border-gray-200"}`} data-testid={`machine-card-${machine.code}`}>
              <CardContent className="p-3 lg:p-4 space-y-2.5">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-bold text-[#0A0A0A] text-sm lg:text-base leading-snug">{machine.code} — {machine.name}</p>
                    <p className="text-[10px] lg:text-xs text-slate-400 font-mono">SN {machine.serial}</p>
                  </div>
                  {down ? (
                    <Badge className="bg-red-600 text-white text-[10px] whitespace-nowrap shrink-0">OUT OF SERVICE</Badge>
                  ) : (
                    <Badge className="bg-emerald-50 text-emerald-700 text-[10px] shrink-0">Active</Badge>
                  )}
                </div>
                {down && (
                  <div className="text-xs text-red-700 bg-red-100 rounded-md p-2 flex items-start gap-1.5">
                    <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                    <span>{machine.status_note}</span>
                  </div>
                )}
                <div className="grid grid-cols-3 gap-1.5 text-center text-[10px] lg:text-xs">
                  <div className="bg-white border border-gray-100 rounded-lg p-1.5 lg:p-2">
                    <p className="text-slate-400 mb-1 whitespace-nowrap">Vacuum</p>
                    <ResultBadge result={vacuum_today?.result} />
                  </div>
                  <div className="bg-white border border-gray-100 rounded-lg p-1.5 lg:p-2">
                    <p className="text-slate-400 mb-1 whitespace-nowrap">Bowie-Dick</p>
                    <ResultBadge result={bowie_dick_today?.result} />
                  </div>
                  <div className="bg-white border border-gray-100 rounded-lg p-1.5 lg:p-2">
                    <p className="text-slate-400 mb-1 whitespace-nowrap">Last BI</p>
                    <ResultBadge result={last_biological?.result} biological />
                    {last_biological && <p className="text-[9px] lg:text-[10px] text-slate-400 mt-1 whitespace-nowrap">{last_biological.date}</p>}
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <p className="text-[11px] lg:text-xs text-slate-500 whitespace-nowrap">Cycles today: <span className="font-bold text-[#655dc6]">{cycles_today}</span></p>
                  {down && (
                    <Button size="sm" variant="outline" className="h-7 text-xs border-emerald-300 text-emerald-700 hover:bg-emerald-50" onClick={() => reactivateMachine(machine)} data-testid={`reactivate-${machine.code}`}>
                      <Power className="w-3 h-3 mr-1" /> Reactivate
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList className="mb-4">
          <TabsTrigger value="tests" data-testid="tab-daily-tests">Daily Tests</TabsTrigger>
          <TabsTrigger value="cycles" data-testid="tab-cycles">Cycle Log</TabsTrigger>
          <TabsTrigger value="biological" data-testid="tab-biological">Biological (BI)</TabsTrigger>
        </TabsList>

        {/* ===== DAILY TESTS ===== */}
        <TabsContent value="tests" className="space-y-3">
          <div className="flex gap-2 flex-wrap">
            <Button className="bg-[#655dc6] hover:bg-[#5049a3] text-white gap-1.5" onClick={() => { setTestForm({ machine_id: "", result: "pass", notes: "" }); setTestDialog("vacuum"); }} data-testid="add-vacuum-test-btn">
              <Wind className="w-4 h-4" /> Record Vacuum Test
            </Button>
            <Button className="bg-[#655dc6] hover:bg-[#5049a3] text-white gap-1.5" onClick={() => { setTestForm({ machine_id: "", result: "pass", notes: "" }); setTestDialog("bowie_dick"); }} data-testid="add-bowiedick-test-btn">
              <PackageOpen className="w-4 h-4" /> Record Bowie-Dick Test
            </Button>
          </div>
          <TestsTable tests={tests.filter(t => t.test_type !== "biological")} isAdmin={isAdmin} onDelete={(id) => deleteRow("tests", id)} />
        </TabsContent>

        {/* ===== CYCLES ===== */}
        <TabsContent value="cycles" className="space-y-3">
          <Button className="bg-[#655dc6] hover:bg-[#5049a3] text-white gap-1.5" onClick={() => setCycleDialog(true)} data-testid="add-cycle-btn">
            <Plus className="w-4 h-4" /> Log Cycle
          </Button>
          <div className="border rounded-lg overflow-x-auto bg-white">
            <Table>
              <TableHeader>
                <TableRow className="text-xs">
                  <TableHead className="whitespace-nowrap">Date</TableHead>
                  <TableHead>#</TableHead>
                  <TableHead className="whitespace-nowrap">Machine</TableHead>
                  <TableHead className="whitespace-nowrap">Program</TableHead>
                  <TableHead className="whitespace-nowrap">Contents</TableHead>
                  <TableHead>CI</TableHead>
                  <TableHead className="whitespace-nowrap">Status</TableHead>
                  <TableHead className="whitespace-nowrap">Operator</TableHead>
                  {isAdmin && <TableHead />}
                </TableRow>
              </TableHeader>
              <TableBody>
                {cycles.length === 0 && (
                  <TableRow><TableCell colSpan={9} className="text-center text-slate-400 py-8">No cycles logged for {month}</TableCell></TableRow>
                )}
                {cycles.map(c => (
                  <TableRow key={c.id} data-testid={`cycle-row-${c.id}`} className="text-xs">
                    <TableCell className="whitespace-nowrap py-2">{c.date}</TableCell>
                    <TableCell className="font-bold text-[#655dc6] py-2">{c.cycle_number}</TableCell>
                    <TableCell className="whitespace-nowrap py-2">{c.machine_code}</TableCell>
                    <TableCell className="py-2 whitespace-nowrap">{c.program_label}</TableCell>
                    <TableCell className="py-2 max-w-[180px] truncate" title={c.contents || ""}>{c.contents || "—"}</TableCell>
                    <TableCell className="py-2">{c.chemical_indicator_pass ? <CheckCircle2 className="w-4 h-4 text-emerald-500" /> : <XCircle className="w-4 h-4 text-red-500" />}</TableCell>
                    <TableCell className="py-2">
                      {c.status === "completed"
                        ? <Badge className="bg-emerald-50 text-emerald-700 text-[10px]">Completed</Badge>
                        : <Badge className="bg-red-50 text-red-700 text-[10px]">Failed</Badge>}
                    </TableCell>
                    <TableCell className="py-2 whitespace-nowrap">{c.operator_name}</TableCell>
                    {isAdmin && (
                      <TableCell>
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-400 hover:text-red-600" onClick={() => deleteRow("cycles", c.id)}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {/* ===== BIOLOGICAL ===== */}
        <TabsContent value="biological" className="space-y-3">
          <Card className="border border-amber-200 bg-amber-50/60 rounded-xl shadow-none">
            <CardContent className="pt-4 text-xs text-amber-800 space-y-1">
              <p className="font-bold text-sm">Weekly BI Protocol (every Saturday)</p>
              <p>1. Run BI vial with 134°C Universal B (50 min) → rest vial 10 min → shake 3–5 s → incubate 24 min (3M ATTEST 490H).</p>
              <p>2. Negative (−) = PASS, instruments released. Positive (+) = re-test a 2nd vial immediately.</p>
              <p>3. 2nd Positive (+) = machine OUT OF SERVICE, call engineer, RECALL &amp; re-pouch &amp; re-sterilize all instruments since last negative BI.</p>
              <p>4. Communication chain on 2nd positive: inform Medical Director, HR Manager, Chief Nurse, Quality Manager and Dentists/Nurses (an urgent alert is raised automatically in the system).</p>
            </CardContent>
          </Card>
          <Button className="bg-[#655dc6] hover:bg-[#5049a3] text-white gap-1.5" onClick={() => { setTestForm({ machine_id: "", result: "pass", notes: "" }); setTestDialog("biological"); }} data-testid="add-bi-test-btn">
            <FlaskConical className="w-4 h-4" /> Record BI Test
          </Button>
          <TestsTable tests={tests.filter(t => t.test_type === "biological")} isAdmin={isAdmin} biological onDelete={(id) => deleteRow("tests", id)} />
        </TabsContent>
      </Tabs>

      {/* Cycle dialog */}
      <Dialog open={cycleDialog} onOpenChange={setCycleDialog}>
        <DialogContent dir="ltr" className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#655dc6]">Log Sterilization Cycle</DialogTitle>
            <DialogDescription>Record one autoclave load/cycle</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Machine *</Label>
              <Select value={cycleForm.machine_id} onValueChange={(v) => setCycleForm({ ...cycleForm, machine_id: v })}>
                <SelectTrigger data-testid="cycle-machine-select"><SelectValue placeholder="Select autoclave" /></SelectTrigger>
                <SelectContent>
                  {autoclaves.map(m => (
                    <SelectItem key={m.id} value={m.id} disabled={m.status === "out_of_service"}>
                      {m.code} — {m.name} (SN {m.serial}){m.status === "out_of_service" ? " — OUT OF SERVICE" : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Program *</Label>
              <Select value={cycleForm.program} onValueChange={(v) => setCycleForm({ ...cycleForm, program: v })}>
                <SelectTrigger data-testid="cycle-program-select"><SelectValue placeholder="Select program" /></SelectTrigger>
                <SelectContent>
                  {programs.map(p => <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Load contents</Label>
              <Input value={cycleForm.contents} onChange={(e) => setCycleForm({ ...cycleForm, contents: e.target.value })} placeholder="e.g. Basic sets x4, Surgical x2" data-testid="cycle-contents-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Chemical Indicator</Label>
                <Select value={cycleForm.chemical_indicator_pass ? "pass" : "fail"} onValueChange={(v) => setCycleForm({ ...cycleForm, chemical_indicator_pass: v === "pass" })}>
                  <SelectTrigger data-testid="cycle-ci-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pass">Pass (color changed)</SelectItem>
                    <SelectItem value="fail">Fail</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Cycle status</Label>
                <Select value={cycleForm.status} onValueChange={(v) => setCycleForm({ ...cycleForm, status: v })}>
                  <SelectTrigger data-testid="cycle-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="failed">Failed / Error</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Notes</Label>
              <Textarea value={cycleForm.notes} onChange={(e) => setCycleForm({ ...cycleForm, notes: e.target.value })} className="min-h-[60px]" data-testid="cycle-notes-input" />
            </div>
            <Button onClick={submitCycle} disabled={submitting} className="w-full bg-[#655dc6] hover:bg-[#5049a3] text-white" data-testid="submit-cycle-btn">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save Cycle"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Test dialog */}
      <Dialog open={!!testDialog} onOpenChange={(o) => !o && setTestDialog(null)}>
        <DialogContent dir="ltr" className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#655dc6]">
              Record {testDialog ? TEST_META[testDialog].label : ""}
            </DialogTitle>
            <DialogDescription>{testDialog ? TEST_META[testDialog].freq : ""} — a 2nd failure today will set the machine OUT OF SERVICE automatically</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Machine *</Label>
              <Select value={testForm.machine_id} onValueChange={(v) => setTestForm({ ...testForm, machine_id: v })}>
                <SelectTrigger data-testid="test-machine-select"><SelectValue placeholder="Select autoclave" /></SelectTrigger>
                <SelectContent>
                  {autoclaves.map(m => (
                    <SelectItem key={m.id} value={m.id}>{m.code} — {m.name} (SN {m.serial})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Result *</Label>
              <Select value={testForm.result} onValueChange={(v) => setTestForm({ ...testForm, result: v })}>
                <SelectTrigger data-testid="test-result-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="pass">{testDialog === "biological" ? "Negative (−) — Pass" : "Pass"}</SelectItem>
                  <SelectItem value="fail">{testDialog === "biological" ? "Positive (+) — Fail" : "Fail / Error"}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Notes</Label>
              <Textarea value={testForm.notes} onChange={(e) => setTestForm({ ...testForm, notes: e.target.value })} className="min-h-[60px]" placeholder={testDialog === "biological" ? "BI lot no., incubator slot..." : ""} data-testid="test-notes-input" />
            </div>
            <Button onClick={submitTest} disabled={submitting} className="w-full bg-[#655dc6] hover:bg-[#5049a3] text-white" data-testid="submit-test-btn">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save Test"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const TestsTable = ({ tests, isAdmin, biological = false, onDelete }) => (
  <div className="border rounded-lg overflow-x-auto bg-white">
    <Table>
      <TableHeader>
        <TableRow className="text-xs">
          <TableHead className="whitespace-nowrap">Date</TableHead>
          {!biological && <TableHead className="whitespace-nowrap">Test</TableHead>}
          <TableHead className="whitespace-nowrap">Machine</TableHead>
          <TableHead className="whitespace-nowrap">Result</TableHead>
          <TableHead className="whitespace-nowrap">Attempt</TableHead>
          <TableHead className="whitespace-nowrap">Action Taken</TableHead>
          <TableHead className="whitespace-nowrap">Notes</TableHead>
          <TableHead className="whitespace-nowrap">Operator</TableHead>
          {isAdmin && <TableHead />}
        </TableRow>
      </TableHeader>
      <TableBody>
        {tests.length === 0 && (
          <TableRow><TableCell colSpan={9} className="text-center text-slate-400 py-8">No tests recorded for this month</TableCell></TableRow>
        )}
        {tests.map(t => (
          <TableRow key={t.id} data-testid={`test-row-${t.id}`} className="text-xs">
            <TableCell className="whitespace-nowrap py-2">{t.date}</TableCell>
            {!biological && <TableCell className="py-2 whitespace-nowrap">{TEST_META[t.test_type]?.label || t.test_type}</TableCell>}
            <TableCell className="whitespace-nowrap py-2">{t.machine_code}</TableCell>
            <TableCell className="py-2">{<ResultBadge result={t.result} biological={t.test_type === "biological"} />}</TableCell>
            <TableCell className="py-2">{t.attempt}</TableCell>
            <TableCell className="py-2 whitespace-nowrap">
              {t.machine_shutdown
                ? <Badge className="bg-red-600 text-white text-[10px]">{t.recall_required ? "SHUTDOWN + RECALL" : "MACHINE SHUTDOWN"}</Badge>
                : t.result === "fail" ? <span className="text-amber-600">Retest required</span> : "—"}
            </TableCell>
            <TableCell className="py-2 max-w-[160px] truncate" title={t.notes || ""}>{t.notes || "—"}</TableCell>
            <TableCell className="py-2 whitespace-nowrap">{t.operator_name}</TableCell>
            {isAdmin && (
              <TableCell>
                <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-400 hover:text-red-600" onClick={() => onDelete(t.id)}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </TableCell>
            )}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </div>
);
